import { Button } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HouseSelector } from '../../components/HouseSelector';
import { useOwnedTenants } from '../../hooks/useOwnedTenants';
import { supabase } from '../../lib/supabase';

/** Typed bridge for set_bidder_card_policy until database.ts regenerates. */
type CardPolicyRpc = (
  fn: 'set_bidder_card_policy',
  args: { p_require: boolean; p_amount_cents: number | null; p_tenant_id: string },
) => Promise<{ data: unknown; error: { message: string } | null }>;
const cardPolicyRpc = supabase.rpc.bind(supabase) as unknown as CardPolicyRpc;

/**
 * Onboarding — bidder CSV import (spec §3). Two phases against the import-bidders
 * EF: dry-run (parse/validate/dedupe → a NON-PII summary the owner reviews) then
 * commit (stage the deduped rows; invites go out at go-live). The CSV uploads
 * DIRECTLY to the EF (owner JWT) — never through the copilot/LLM.
 */

interface ImportSummary {
  rows_read: number;
  rows_valid: number;
  rows_invalid: number;
  rows_deduped: number;
  unique_bidders: number;
  bidder_number_collisions: Array<[number, number]>;
  errors_by_kind: Record<string, number>;
  sample_errors: Array<{ row_num: number; errors: string[] }>;
}

type ImportResponse = { mode?: string; summary?: ImportSummary; staged?: number; error?: string };

export function AdminBidderImport() {
  const owned = useOwnedTenants();
  const tenantId = owned.selectedId;
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [staged, setStaged] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Require a card on file" onboarding preference (set BEFORE upload). New
  // auctions default to a card hold of this amount via the auction-insert
  // trigger; owner-overridable per auction.
  const [requireCard, setRequireCard] = useState(false);
  const [amountDollars, setAmountDollars] = useState('');
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [policySaved, setPolicySaved] = useState(false);
  const selRequire = owned.selected?.require_bidder_card_on_file ?? false;
  const selAmountCents = owned.selected?.default_cc_preauth_amount_cents ?? null;
  useEffect(() => {
    setRequireCard(selRequire);
    setAmountDollars(selAmountCents != null ? (selAmountCents / 100).toString() : '');
    setPolicySaved(false);
    setPolicyError(null);
  }, [tenantId, selRequire, selAmountCents]);

  async function savePolicy(): Promise<void> {
    if (!tenantId) return;
    const cents = requireCard ? Math.round(Number.parseFloat(amountDollars || '0') * 100) : null;
    if (requireCard && (!cents || cents <= 0)) {
      setPolicyError('Enter a hold amount greater than $0.');
      return;
    }
    setPolicySaving(true);
    setPolicyError(null);
    setPolicySaved(false);
    const { error: pErr } = await cardPolicyRpc('set_bidder_card_policy', {
      p_require: requireCard,
      p_amount_cents: cents,
      p_tenant_id: tenantId,
    });
    setPolicySaving(false);
    if (pErr) {
      setPolicyError('Could not save that setting — please try again.');
    } else {
      setPolicySaved(true);
    }
  }

  async function callImport(mode: 'dry_run' | 'commit', text: string): Promise<void> {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    const res = await supabase.functions.invoke<ImportResponse>('import-bidders', {
      body: { mode, csv: text, tenant_id: tenantId },
    });
    setBusy(false);
    if (res.error) {
      setError(
        res.error instanceof Error ? res.error.message : 'Import failed — please try again.',
      );
      return;
    }
    const data = res.data;
    if (data?.error) {
      setError(friendlyError(data.error));
      return;
    }
    if (data?.summary) setSummary(data.summary);
    if (mode === 'commit') setStaged(data?.staged ?? 0);
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    setSummary(null);
    setStaged(null);
    setError(null);
    const text = await file.text();
    setCsv(text);
    setFileName(file.name);
    await callImport('dry_run', text);
  }

  const canCommit = summary !== null && summary.unique_bidders > 0 && staged === null;

  if (owned.loading) return <div style={wrapStyle}>Loading…</div>;
  if (!tenantId) {
    return (
      <div style={wrapStyle}>
        <p style={{ color: 'var(--text-muted)' }}>Bidder import is available to tenant owners.</p>
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <header style={{ marginBottom: '1.25rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Bring your bidders
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.3rem', maxWidth: '42rem' }}>
          Upload a CSV of your existing bidders (columns: email, first_name, last_name, phone,
          bidder_number, status, tags, notes). We’ll check it and show you a summary before anything
          is saved. Invitations go out when you go live — not now.{' '}
          <Link to="/admin/onboarding" style={{ color: 'var(--brand-link)' }}>
            Back to setup
          </Link>
        </p>
      </header>

      <HouseSelector
        tenants={owned.tenants}
        selectedId={owned.selectedId}
        onSelect={owned.setSelectedId}
        disabled={busy}
      />

      <section style={cardStyle}>
        <label
          style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={requireCard}
            disabled={busy || policySaving}
            onChange={(e) => {
              setRequireCard(e.target.checked);
              setPolicySaved(false);
            }}
            style={{ marginTop: '0.2rem' }}
          />
          <span>
            <strong style={{ color: 'var(--text)' }}>Require a card on file before bidding</strong>
            <p style={{ color: 'var(--text-muted)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
              Bidders place a temporary hold on their card before they can bid. New auctions you
              create will default to this — you can change it on any individual auction. Off:
              bidders can bid as soon as they sign in.
            </p>
          </span>
        </label>

        {requireCard && (
          <div
            style={{
              margin: '0.6rem 0 0 1.85rem',
              display: 'flex',
              gap: '0.4rem',
              alignItems: 'center',
              fontSize: '0.9rem',
              color: 'var(--text)',
            }}
          >
            <span>Hold amount</span>
            <span style={{ color: 'var(--text-muted)' }}>$</span>
            <input
              type="number"
              min="1"
              step="1"
              inputMode="decimal"
              value={amountDollars}
              disabled={busy || policySaving}
              onChange={(e) => {
                setAmountDollars(e.target.value);
                setPolicySaved(false);
              }}
              style={{
                width: '7rem',
                padding: '0.3rem 0.5rem',
                borderRadius: 'var(--r-tile, 0.375rem)',
                border: '1px solid var(--admin-hairline, var(--border))',
                background: 'var(--surface)',
                color: 'var(--text)',
              }}
            />
          </div>
        )}

        <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <Button onClick={() => void savePolicy()} disabled={busy || policySaving}>
            {policySaving ? 'Saving…' : 'Save policy'}
          </Button>
          {policySaved && (
            <span style={{ color: 'var(--semantic-success)', fontSize: '0.85rem' }}>Saved.</span>
          )}
        </div>
        {policyError && (
          <p
            role="alert"
            style={{ color: 'var(--semantic-warning)', margin: '0.5rem 0 0', fontSize: '0.85rem' }}
          >
            {policyError}
          </p>
        )}
      </section>

      <section style={cardStyle}>
        <label style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.4rem' }}>
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>Choose your bidder CSV</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => void onFile(e)}
            disabled={busy}
          />
        </label>
        {fileName && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
            {fileName}
            {busy ? ' — checking…' : ''}
          </p>
        )}
      </section>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {staged !== null ? (
        <section style={cardStyle}>
          <h2
            style={{
              margin: 0,
              fontSize: '1.05rem',
              fontWeight: 600,
              color: 'var(--semantic-success)',
            }}
          >
            ✓ {staged} bidder{staged === 1 ? '' : 's'} staged
          </h2>
          <p style={{ color: 'var(--text-muted)', margin: '0.4rem 0 0', fontSize: '0.9rem' }}>
            They’ll be invited to your site when you go live.{' '}
            <Link to="/admin/onboarding" style={{ color: 'var(--brand-link)' }}>
              Back to setup
            </Link>
          </p>
        </section>
      ) : summary ? (
        <section style={cardStyle}>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)' }}>
            Review — {summary.unique_bidders} unique bidder{summary.unique_bidders === 1 ? '' : 's'}
          </h2>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0.6rem 0',
              display: 'grid',
              gap: '0.25rem',
              fontSize: '0.9rem',
            }}
          >
            <li>Rows read: {summary.rows_read}</li>
            <li>Valid: {summary.rows_valid}</li>
            {summary.rows_invalid > 0 && (
              <li style={{ color: 'var(--semantic-warning)' }}>
                Skipped (errors): {summary.rows_invalid}
              </li>
            )}
            {summary.rows_deduped > 0 && <li>Duplicate emails merged: {summary.rows_deduped}</li>}
            {summary.bidder_number_collisions.length > 0 && (
              <li style={{ color: 'var(--semantic-warning)' }}>
                Bidder-number clashes: {summary.bidder_number_collisions.length} (we’ll keep the
                first)
              </li>
            )}
          </ul>
          {summary.sample_errors.length > 0 && (
            <details style={{ margin: '0.25rem 0 0.75rem' }}>
              <summary
                style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.85rem' }}
              >
                {summary.rows_invalid} row(s) with problems
              </summary>
              <ul style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                {summary.sample_errors.map((se) => (
                  <li key={se.row_num}>
                    Row {se.row_num}: {se.errors.join(', ')}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              onClick={() => {
                if (csv) void callImport('commit', csv);
              }}
              disabled={!canCommit || busy}
            >
              {busy ? 'Importing…' : `Import ${summary.unique_bidders} bidders`}
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function friendlyError(code: string): string {
  switch (code) {
    case 'csv_too_large':
      return 'That file is too large (max 5 MB).';
    case 'too_many_rows':
      return 'That file has too many rows (max 100,000).';
    case 'not_a_tenant_owner':
    case 'not_tenant_owner':
      return 'Only the tenant owner can import bidders.';
    case 'missing_csv':
      return 'The file appears to be empty.';
    default:
      return `Import failed: ${code}`;
  }
}

const wrapStyle: React.CSSProperties = { padding: '2rem 2.25rem', maxWidth: '52rem' };

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
  marginBottom: '1rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
};
